import { Bob } from "../stores/Bob";

import { formatFiat } from "./formatNumbers";
import { makeSpreadsheetLineForOurInvoice } from "./invoices";
import { makeSpreadsheetLineForOnchainInbound } from "./onchainInboundTransactions";
import { makeSpreadsheetLineForOnchainOutbound } from "./onchainOutboundTransactions";
import { makeSpreadsheetLineForStablePayment } from "./stablePayments";
import { makeSpreadsheetLineForPlatformCredit } from "./platformCredits";
import { CurrencySnapshot } from "./types";

// types.ts (or wherever you keep shared types)
export interface SpreadsheetLineInput {
  type: string;
  status: string;
  uuid: string;
  createdAtInteger: number;
  currencySnapshot: CurrencySnapshot;
  currency: string;
  amountSatoshis: number | null;
  isCredit: boolean;
  comment: string | null;
  payRequest: string | null;
  routingFeeSatoshis: number | null;
  platformFeeSatoshis: number | null;
  lightningAddress?: string | null; // Optional field for lightning address
  // Onchain-specific fields
  onchainTransactionId?: string | null;
  onchainReceivingAddress?: string | null;
  onchainConfirmations?: number | null;
}

// ...existing code...
export const makeSpreadsheetLine = ({
  type,
  status,
  uuid,
  createdAtInteger,
  currencySnapshot,
  currency,
  amountSatoshis,
  isCredit,
  comment,
  payRequest,
  routingFeeSatoshis,
  platformFeeSatoshis,
  lightningAddress,
  onchainTransactionId,
  onchainReceivingAddress,
  onchainConfirmations,
}: SpreadsheetLineInput) => {
  const hash = {};
  hash["type"] = type;
  hash["status"] = status;
  hash["id"] = uuid;
  const createdAt = new Date(createdAtInteger);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(createdAt);
  // Assuming en-US format parts: month (0), / (1), day (2), / (3), year (4), , (5), hour (6), : (7), minute (8), : (9), second (10)
  hash["created_at"] =
    `${parts[4].value}-${parts[0].value}-${parts[2].value} ${parts[6].value}:${parts[8].value}:${parts[10].value}`;
  hash["time_zone"] = timeZone;
  hash["exchange_rate"] = currencySnapshot[currency];
  hash["currency"] = currency.toUpperCase();
  hash["amount"] =
    amountSatoshis === null || amountSatoshis === undefined
      ? "0"
      : isCredit
        ? amountSatoshis.toLocaleString()
        : (-amountSatoshis).toLocaleString();
  // hash["payRequest"] = payRequest || "";
  hash["comment"] = comment || "";
  hash["routing_fee"] = routingFeeSatoshis
    ? routingFeeSatoshis.toLocaleString()
    : "";
  hash["platform_fee"] = platformFeeSatoshis
    ? platformFeeSatoshis.toLocaleString()
    : "";
  hash["amount_fiat"] = formatFiat({
    satoshis: amountSatoshis,
    currency,
    currencySnapshot,
    isNegative: !isCredit,
  });
  hash["routing_fee_fiat"] = routingFeeSatoshis
    ? formatFiat({
        satoshis: routingFeeSatoshis,
        currency,
        currencySnapshot,
        isNegative: false,
      })
    : "";
  hash["platform_fee_fiat"] = platformFeeSatoshis
    ? formatFiat({
        satoshis: platformFeeSatoshis,
        currency,
        currencySnapshot,
        isNegative: false,
      })
    : "";
  hash["invoice"] = payRequest || "";
  hash["timestamp"] = createdAtInteger; // For sorting purposes
  hash["lightning_address"] = lightningAddress;
  // Onchain-specific fields (will be empty for non-onchain transactions)
  hash["onchain_txid"] = onchainTransactionId || "";
  hash["onchain_address"] = onchainReceivingAddress || "";
  hash["onchain_confirmations"] =
    onchainConfirmations !== null && onchainConfirmations !== undefined
      ? onchainConfirmations.toString()
      : "";
  return hash;
};

export const makeSpreadsheetLines = (bob: Bob) => {
  const lines = [];
  // Our Invoices
  bob.ourInvoices.data.forEach((invoice) => {
    lines.push(makeSpreadsheetLineForOurInvoice(invoice, bob));
  });
  // Platform adjustments
  bob.platformCredits.data.forEach((credit) => {
    lines.push(makeSpreadsheetLineForPlatformCredit(credit, bob));
  });
  // Pay Attempts
  bob.payAttemptsPersisted.forEach((payAttempt) => {
    lines.push(payAttempt.spreadsheetLine);
  });
  // Onchain Inbound Transactions
  bob.ourOnchainInboundTransactions.data.forEach((tx) => {
    lines.push(makeSpreadsheetLineForOnchainInbound(tx, bob));
  });
  // Onchain Outbound Transactions
  bob.ourOnchainOutboundTransactions.data.forEach((tx) => {
    lines.push(makeSpreadsheetLineForOnchainOutbound(tx, bob));
  });
  // Stable Payments (USDT, etc.)
  bob.ourStablePayments.data.forEach((tx) => {
    lines.push(makeSpreadsheetLineForStablePayment(tx, bob));
  });
  // Sort by timestamp/createdAtInteger in descending order (newest first)
  const sortedLines = lines.sort((a, b) => {
    const aTime = a.timestamp;
    const bTime = b.timestamp;
    return bTime - aTime;
  });
  const headers = [
    "type",
    "status",
    "id",
    "created_at",
    "time_zone",
    "exchange_rate",
    "currency",
    "amount",
    "comment",
    "routing_fee",
    "platform_fee",
    "amount_fiat",
    "routing_fee_fiat",
    "platform_fee_fiat",
    "lightning_address",
    "invoice",
    "onchain_txid",
    "onchain_address",
    "timestamp",
  ];
  const dataRows = sortedLines.map((line) => headers.map((key) => line[key]));
  return [headers, ...dataRows];
};
// ...existing code...
